import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import MultiQueryPageLoader from '@/app/common/components/MultiQueryPageLoader'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import { buttonVariants } from '@/app/common/components/button/buttonVariants'
import { WarningIcon } from '@/app/common/icons/support'
import { academicTermsQueries } from '@/queries/academic_terms/queries'
import { ProgramQueries } from '@/queries/program/queries'
import { selectSelectedProgram } from '@/storage/programs/slice'
import {
  getAcademicTermsByTermType,
  getSelectedProgramYear,
} from '@/app/pages/academic_terms/utils'
import AcademicTermsView from '@/app/pages/academic_terms/view_academic_terms/_components/academic-terms-view'
import AcademicTermsForm from '@/app/pages/academic_terms/create_academic_term/_components/create-academic-term-form/create-academic-term-form'
import { PurseDetailOutletContext } from './purse-detail-view'

export function AcademicTermsTabLoader() {
  const { purse } = useOutletContext<PurseDetailOutletContext>()
  const selectedProgramYearId = useSelector(selectSelectedProgram).id
  const [step, setStep] = useState<'view' | 'create'>('view')

  const programAcademicTermsQuery = useQuery(
    academicTermsQueries.getProgramPurseAcademicTerms(
      selectedProgramYearId,
      purse.id
    )
  )
  const globalAcademicTermsQuery = useQuery(
    academicTermsQueries.getGlobalAcademicTerms()
  )
  const programDetailsQuery = useQuery(ProgramQueries.FetchGlobalPrograms())

  return (
    <MultiQueryPageLoader
      queries={
        [
          programAcademicTermsQuery,
          globalAcademicTermsQuery,
          programDetailsQuery,
        ] as const
      }
    >
      {([academicTerms, globalAcademicTerms, programDetails]) => {
        const selectedProgram = getSelectedProgramYear(
          programDetails,
          selectedProgramYearId
        )

        if (!selectedProgram) {
          return (
            <Card>
              <CardContent>
                <WarningIcon className="text-3xl text-primary-500" />
                <h3 className="text-2xl font-medium text-gray-800">
                  Selected program year not found
                </h3>
                <p>Please contact support to get help with this issue.</p>
              </CardContent>
            </Card>
          )
        }

        const selectedAcademicTerms = getAcademicTermsByTermType(
          academicTerms,
          purse
        )

        if (step === 'create') {
          const hasActiveTerm = selectedAcademicTerms.find(
            (term) => term.status_name === 'Active'
          )

          if (hasActiveTerm) {
            return (
              <div className="pt-20 max-w-lg mx-auto">
                <Card>
                  <CardHeader>
                    <WarningIcon className="text-3xl text-primary-500" />
                    <h3 className="text-2xl font-medium text-gray-800">
                      Term system is locked for your current school year.
                    </h3>
                  </CardHeader>
                  <CardContent className="space-y-4 text-gray-700">
                    <p>
                      Why is this locked? Once a term becomes active, the system
                      is locked for the entire school year in this purse to
                      maintain consistency for students and financial aid.
                    </p>
                    <p>
                      You can select a different system when setting up the next
                      school year.
                    </p>
                    <div className="pt-6">
                      <button
                        onClick={() => setStep('view')}
                        className={buttonVariants({
                          variant: 'primary',
                          size: 'lg',
                          className: 'w-full',
                        })}
                      >
                        Ok Understood, Take me back
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )
          }

          return (
            <AcademicTermsForm
              programSettings={selectedProgram}
              programAcademicTerms={selectedAcademicTerms}
              globalAcademicTerms={globalAcademicTerms}
              purse={purse}
              onSuccess={() => setStep('view')}
            />
          )
        }

        return (
          <AcademicTermsView
            programAcademicTerms={selectedAcademicTerms}
            globalAcademicTerms={globalAcademicTerms}
            programSettings={selectedProgram}
            purse={purse}
            onSetupClick={() => setStep('create')}
          />
        )
      }}
    </MultiQueryPageLoader>
  )
}
